import { TextInput } from '@d-edge/overnight-hotelier-react'
import { useMemo, useState } from 'react'
import { useConfig, useProductionResources } from '@/api/queries'
import type { ProductionResource, ResourceType } from '@/api/types'
import { Badge } from '@/components/ui/Badge'
import { IconButton } from '@/components/ui/Button'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { KeyValueList } from '@/components/ui/Field'
import { Icons } from '@/components/ui/Icons'
import { LoadingBlock } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { useLocalStorage } from '@/lib/hooks'
import { links } from '@/lib/links'
import styles from './ResourcesPage.module.css'

const TYPE_LABELS: Record<ResourceType, string> = {
  AppService: 'App Service',
  FunctionApp: 'Function App',
  CloudService: 'Cloud Service',
  Kubernetes: 'Kubernetes (EKS)',
  ContainerInstance: 'Container Instance',
  Lambda: 'AWS Lambda',
  StaticSite: 'Static site',
  Iis: 'IIS',
  ElasticBeanstalk: 'Elastic Beanstalk',
  Ec2: 'AWS EC2',
}

const matches = (resource: ProductionResource, search: string) =>
  [resource.name, resource.product, resource.repository, resource.bugsnagProject ?? ''].some((value) => value.toLowerCase().includes(search))

function CopyableValue({ value, label, href }: { value: string; label: string; href?: string }) {
  const toast = useToast()
  const copy = () =>
    navigator.clipboard.writeText(value).then(
      () => toast.notify(`${label} copied`),
      (error: unknown) => toast.error(error, 'Copy failed'),
    )

  return (
    <span className={styles.value}>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" className={styles.code}>
          {value}
        </a>
      ) : (
        <span className={styles.code}>{value}</span>
      )}
      <IconButton label={`Copy ${label.toLowerCase()}`} onClick={() => void copy()}>
        <Icons.Copy size={14} />
      </IconButton>
    </span>
  )
}

function Details({ resource }: { resource: ProductionResource }) {
  const { data: config } = useConfig()
  return (
    <section className={styles.details} aria-labelledby="resource-name">
      <header className={styles.detailsHeader}>
        <h2 id="resource-name" className={styles.detailsTitle}>
          {resource.name}
        </h2>
        <Badge tone="info">{resource.product}</Badge>
        <Badge tone="neutral">{TYPE_LABELS[resource.type]}</Badge>
      </header>
      <KeyValueList
        items={[
          {
            label: 'Base address',
            value: resource.baseUrl ? <CopyableValue value={resource.baseUrl} label="Base address" href={resource.baseUrl} /> : <span className="u-faint">No HTTP endpoint</span>,
          },
          {
            label: 'Bugsnag project',
            value: resource.bugsnagProject ? (
              <CopyableValue value={resource.bugsnagProject} label="Bugsnag project" href={links.bugsnagProject(config?.links, resource.bugsnagProject, resource.bugsnagSlug)} />
            ) : <span className="u-faint">Unknown</span>,
          },
          { label: 'Repository', value: <span className={styles.code}>{resource.repository}</span> },
          ...(resource.notes ? [{ label: 'Notes', value: resource.notes }] : []),
        ]}
      />
    </section>
  )
}

/** Production resources with the base address of their endpoints and their Bugsnag project. */
export function ResourcesPage() {
  const { data: resources, isLoading, error } = useProductionResources()
  const [selectedName, setSelectedName] = useLocalStorage<string>('resources.selected', '')
  const [search, setSearch] = useState('')

  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const byProduct = new Map<string, ProductionResource[]>()
    for (const resource of resources ?? []) {
      if (needle && !matches(resource, needle)) continue
      byProduct.set(resource.product, [...(byProduct.get(resource.product) ?? []), resource])
    }
    return [...byProduct.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [resources, search])

  if (error) return <ErrorState error={error} />
  if (isLoading || !resources) return <LoadingBlock />

  const selected = resources.find((r) => r.name === selectedName)

  return (
    <div className={styles.page}>
      <nav className={styles.panel} aria-label="Production resources">
        <TextInput fullWidth placeholder="Filter by name, product or repository" aria-label="Filter resources" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className={styles.list}>
          {groups.length === 0 && <EmptyState>No resource matches</EmptyState>}
          {groups.map(([product, items]) => (
            <div key={product} className={styles.group}>
              <div className={styles.groupTitle}>{product}</div>
              {items.map((resource) => (
                <button
                  key={resource.name}
                  type="button"
                  className={`${styles.item} ${resource.name === selectedName ? styles.itemActive : ''}`}
                  aria-current={resource.name === selectedName}
                  onClick={() => setSelectedName(resource.name)}
                >
                  {resource.name}
                </button>
              ))}
            </div>
          ))}
        </div>
      </nav>

      {selected ? <Details resource={selected} /> : <div className={styles.hint}>Choose a resource to see its base address and Bugsnag project.</div>}
    </div>
  )
}
